import Vue from "vue";
import {ModelType} from '~/enums/modelType';
import {ReferenceNodeType} from '~/enums/referenceNodeType';
import {ObjectHelper} from "~/helpers/objectHelper";
import {GenericType} from '~/interfaces/genericTypeInterfaces';
import {IImposter, IImposterMap, IImposterOptions} from '~/interfaces/imposterInterfaces';
import {IObjectModelRelation} from '~/interfaces/objectModelInterfaces'
import Validatable from "~/mixins/validatable";
import {ComparisonType} from "~/models/conditionalRules/comparisonType";
import {TargetType} from "~/models/conditionalRules/targetType";
import {DatetimeMode} from "~/models/datetimeMode";
import Imposter from '~/models/imposter';
import DefaultImposterMapFactory from '~/models/imposterMapFactories/defaultImposterMapFactory';
import ObjectModelRelationValidation from "~/models/navigableValidations/objectModelRelationValidation";
import ObjectModelPermittable from "~/models/objectModelPermissions/objectModelPermittable";
import ObjectModelDefaultPropertyReference from "~/models/objectModels/objectModelDefaultPropertyReference";
import {RelationType} from "~/models/relationType";
import GloballySelected from "../../../formability/object_model_builder/store/modules/selected";
import assertNever from '../../../global/assetNever';
import {friendlyObjectRelationDisplay, ObjectModelRelationType} from "../../enums/objectModelRelationType";
import {ArrayHelper} from "../../helpers/arrayHelper";
import Guid from "../../helpers/guid";
import {InstanceHelper} from "../../helpers/instanceHelper";
import IRepo from "../../interfaces/repo";
import Repo from "../../state/modules/repo";
import ObjectModel from "../objectModel";
import ObjectModelBuilderModel from "../tracked/objectModelBuilderModel";
import AccountRelation from "./accountRelation";
import EntryRelation from "./entryRelation";
import {ObjectModelRelationCapability} from "./objectModelRelationCapability";
import ObjectModelRelationCapabilityManager from "./objectModelRelationCapabilityManager";
import ObjectModelRelationDefinition from "./objectModelRelationDefinition";
import {AccountRelationUserType} from '~/interfaces/modelTypeInterfaces'
import {AccountType} from "~/enums/accountType"
import ObjectModelRelationUserTypeFilterPermittee from '~/models/objectModelRelations/objectModelRelationUserTypeFilterPermittee'
import {Hash} from '~/interfaces/hashInterfaces'
import ElementHandler from '~/services/elementHandler'
import {ITrackedModel} from '~/interfaces/modelInterfaces'
import ObjectModelRelationPathService from '~/services/referencePathServices/objectModelRelationPathService'
import ListView from "~/models/listViews/listView";
import {PermitteeType} from "~/enums/permitteeType";

export default class ObjectModelRelation extends Validatable(ObjectModelPermittable(ObjectModelBuilderModel)) implements IObjectModelRelation {
  object_model_id: string;
  label_title: string;
  relation_type: ObjectModelRelationType;
  inverse_label_title: string;
  is_required: boolean;
  inverse_dependent_destroy: boolean;
  related_object_model_ids: string[];
  related_object_model_list_view_ids;
  related_object_model_display_properties;
  related_object_model_display_default_properties:  { [key: string]: ObjectModelDefaultPropertyReference} = {} ;
  parent_object_model_display_properties;
  parent_object_model_display_default_properties:{ [key: string]: ObjectModelDefaultPropertyReference} = {} ;
  entry_relations: EntryRelation[] = [];
  inverse: boolean;
  user_type: AccountRelationUserType
  user_type_filters: Hash<ObjectModelRelationUserTypeFilterPermittee> = {}
  _userTypeFiltersHandler: ElementHandler<ObjectModelRelationUserTypeFilterPermittee> = new ElementHandler<ObjectModelRelationUserTypeFilterPermittee>(this, ObjectModelRelation, 'user_type_filters')
  get userTypeFiltersHandler(): ElementHandler<ObjectModelRelationUserTypeFilterPermittee> {
    return this._userTypeFiltersHandler
  }
  portal_for_claiming: string;
  account_relations: AccountRelation[];
  // used to validate account relations that are not restricted to org members
  // these fields are only needed on the front end
  new_email: string = "";
  new_email_confirmation: string = "";
  reference_name: string;

  get related_object_models(): ObjectModel[] {
    if (this.related_object_model_ids) {
      return ObjectModel.repo.findWhere(om => this.related_object_model_ids.includes(om.id));
    } else {
      return [];
    }
  }

  root_object_model: ObjectModel;

  except = ['objectModel', 'createEmbeddedObject', 'inverse', 'related_object_models', 'root_object_model' ,'related_object_model_display_default_properties', 'parent_object_model_display_default_properties'];

  static repo: IRepo<ObjectModelRelation> = new Repo<ObjectModelRelation>('objectModelRelations', ObjectModelRelation);

  constructor() {
    super();
    this.defineProperty('client_id', Guid.create());
  }

  get TRACKED_ARRAYS(): string[] {
    return ["related_object_model_list_view_ids", "related_object_model_display_properties", "parent_object_model_display_properties" ];
  }

  get UNTRACKED_PROPERTIES(): string[] {
    return [
      'entry_relations',
      'related_object_models',
      'root_object_model',
      'inverse',
      'account_relations',
    ];
  }

  get relatedObjectModelDisplayDefaultPropertiesArray(): ObjectModelDefaultPropertyReference[]{
    return ObjectHelper.toArray(this.related_object_model_display_default_properties)
  }

  get parentObjectModelDisplayDefaultPropertiesArray(): ObjectModelDefaultPropertyReference[]{
    return ObjectHelper.toArray(this.parent_object_model_display_default_properties)
  }

  get userTypeFiltersArray(): ObjectModelRelationUserTypeFilterPermittee[] {
    return ObjectHelper.toArray(this.user_type_filters);
  }

  appendRelatedObjectModelDisplayDefaultProperty(json = {}){
    let propertyReference = this.createEmbeddedObject.related_object_model_display_default_properties(this, json );
    this.trackCreationDeletion(propertyReference.client_id, null, propertyReference.asJson(), propertyReference);
    Vue.set(this[propertyReference.rootName], propertyReference.client_id, propertyReference);
  }
  appendParentObjectModelDisplayDefaultProperty(json = {}){
    let propertyReference =  this.createEmbeddedObject.parent_object_model_display_default_properties(this, json )
    this.trackCreationDeletion(propertyReference.client_id, null, propertyReference.asJson(), propertyReference);
    Vue.set(this[propertyReference.rootName], propertyReference.client_id, propertyReference);
  }


  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    const models = {
      'related_object_model_display_default_properties': {type:ObjectModelDefaultPropertyReference , relationType: RelationType.dictionary},
      'parent_object_model_display_default_properties': {type: ObjectModelDefaultPropertyReference, relationType: RelationType.dictionary},
      'user_type_filters': {type: ObjectModelRelationUserTypeFilterPermittee, relationType: RelationType.dictionary},
    };
    return models;
  }

  createEmbeddedObject = {
    related_object_model_display_default_properties: (parent, json): ObjectModelDefaultPropertyReference => new ObjectModelDefaultPropertyReference(parent, 'related_object_model_display_default_properties', json),
    parent_object_model_display_default_properties: (parent, json): ObjectModelDefaultPropertyReference => new ObjectModelDefaultPropertyReference(parent, 'parent_object_model_display_default_properties', json),
    user_type_filters: (parent, json): ObjectModelRelationUserTypeFilterPermittee => {
      return ObjectModelRelationUserTypeFilterPermittee.repo.findOrCreateWith([json.client_id, json.id], parent, json)
    },
  };

  initialize(root: ObjectModel, parent: ObjectModel | ObjectModelRelation = root, relationshipType: ObjectModelRelationType, json: object = {}): this {
    this.rootModel = root;
    this.parent = parent;
    this.object_model_id = root.id;
    this.defineProperty('relation_type', relationshipType);
    ObjectModelRelationCapabilityManager.addDefaultProperties(this);
    this.loadFromJson(json);
    return this;
  }

  static get rootName(): string {
    return 'object_model_relations';
  }

  static get displayName(): string {
    return 'ObjectModelRelation';
  }

  get rootName(): string {
    return 'object_model_relations';
  }

  get isSelected(): boolean {
    return GloballySelected.objectModelRelation.is(this);
  }

  get icon(): string {
    return ObjectModelRelationDefinition.getIcon(this.relation_type);
  }

  get displayName(): string {
    return ObjectModelRelationDefinition.getDisplayName(this.relation_type);
  }

  can(duty: ObjectModelRelationCapability): boolean {
    return ObjectModelRelationCapabilityManager.can(this.relation_type, duty);
  }

  get rootObjectModel(): ObjectModel {
    return ObjectModel.repo.findBy(om => this.root_object_model && om.id === this.root_object_model.id)
  }

  get objectModel(): ObjectModel {
    if (this.parent) {
      if (this.parent instanceof ObjectModel) {
        return this.parent
      } else {
        return this.parent.parent
      }
    }
  }

  get isLast(): boolean {
    return this.parent.sorted_relation_client_ids.indexOf(this.client_id) === this.parent.nextSortOrderFor(this.rootName) - 1;
  }

  get isFirst(): boolean {
    return this.parent.sorted_relation_client_ids.indexOf(this.client_id) <= 0;
  }

  get isAccountRelationship(): boolean {
    return this.isAccountTypeAccountRelationship || this.isEntityAccountRelationship;
  }

  get isAccountTypeAccountRelationship(): boolean {
    return this.relation_type === ObjectModelRelationType.Account;
  }

  get isEntityAccountRelationship(): boolean {
    return this.relation_type === ObjectModelRelationType.EntityAccount;
  }

  get isIndexable(): boolean {
    return this.can(ObjectModelRelationCapability.indexable);
  }

  get isIndexed(): boolean {
    return this.objectModel.isRelationIndexed(this.client_id);
  }

  /**
   * Only applicable to AccountRelationship relations
   */
  get userType(): AccountRelationUserType {
    if (this.isAccountTypeAccountRelationship) return this.user_type
  }

  get accountType(): AccountType {
    if (this.isAccountTypeAccountRelationship) return AccountType.User
    if (this.isEntityAccountRelationship) return AccountType.Entity
  }

  get hasGroupFilters(): boolean {
    return this.userTypeFiltersArray.length > 0 && this.userTypeFiltersArray.every(f => f.reference_type === PermitteeType.group);
  }

  get sortOrder(): number {
    return this.parent.sorted_relation_client_ids.indexOf(this.client_id);
  }

  moveUp(): void {
    if (!this.isFirst) {
      this.parent.updateRelationPositions(this, this.sortOrder - 1);
    }
  }

  moveDown(): void {
    if (!this.isLast) {
      this.parent.updateRelationPositions(this, this.sortOrder + 1);
    }
  }

  addToRepo(): void {
    ObjectModelRelation.repo.add(this);
  }

  get isSelectable(): boolean {
    return true;
  }

  get selector(): typeof GloballySelected['objectModelRelation'] {
    return GloballySelected.objectModelRelation;
  }

  select(): void {
    this.selector.set(this);
  }

  deselect(): void {
    this.selector.deselectIf(this);
  }

  get hasLabelTitle(): boolean {
    return this.label_title !== null && this.label_title.length > 0;
  }

  labelTitleOr(otherwise: string): string {
    return this.label_title || otherwise;
  }

  get labelTitleOrDefault(): string {
    return this.labelTitleOr('relationship');
  }

  inverseLabelTitleOr(otherwise: string): string {
    return this.inverse_label_title || otherwise;
  }

  get inverseLabelTitleOrDefault(): string {
    return this.inverseLabelTitleOr('inverse relationship');
  }

  get labelTitleDisplay(): string {
    if (this.label_title) {
      return this.label_title;
    }
    return 'Unnamed Relationship';
  }

  get inverseLabelTitleDisplay(): string {
    return this.inverseLabelTitleOr('Unnamed Inverse Relationship');
  }

  get permissionDisplay(): string {
    if (this.relation_type === ObjectModelRelationType.Account) {
      return this.labelTitleOr('untitled account');
    }
    if (this.relation_type === ObjectModelRelationType.EntityAccount) {
      return this.labelTitleOr('untitled entity account');
    }
    return this.inverseLabelTitleOr('Unnamed Relationship');
  }

  get friendlyDescription(): string {
    const left = this.hasExternalOrigin ? this.inverseLabelTitleDisplay : this.labelTitleDisplay;
    const right = this.hasExternalOrigin ? this.labelTitleDisplay : this.inverseLabelTitleDisplay;
    return `${left} ${friendlyObjectRelationDisplay(this.relation_type).toLowerCase()} ${right}`;
  }

  get hasExternalOrigin(): boolean {
    return this.root_object_model && this.root_object_model.id !== this.rootModel.id; // objectModel is probably this.rootModel
  }

  get relationType(): ObjectModelRelationType {
    if (this.hasExternalOrigin) {
      return ObjectModelRelationDefinition.inverseRelationshipType(this.relation_type);
    } else {
      return this.relation_type
    }
  }

  get inverseRelationshipTitle(): string {
    if (this.hasExternalOrigin) {
      return this.labelTitleOrDefault
    } else {
      return this.inverseLabelTitleOrDefault
    }
  }


  afterDestroy() {
    super.afterDestroy();
    this.parent.removeObjectModelRelation(this);
  }

  removeRelatedObjectModel(modelOrId: ObjectModel | string): void {
    let id: string;
    if (InstanceHelper.isString(modelOrId)) {
      id = modelOrId;
    } else {
      id = modelOrId.id;
    }
    // reassign so autosave works
    this.related_object_model_ids = ArrayHelper.remove(this.related_object_model_ids, id).slice();
  }

  get publishedEquivalent() {
    return this.objectModel.publishedEquivalent(this.client_id)
  }

  setRelatedObjectModelListViewIds(listViewIdsObj) {
    const newArr = Object.keys(listViewIdsObj).map(key => {
      const obj = {}
      obj[key] = listViewIdsObj[key]
      return obj
    })

    this.related_object_model_list_view_ids = newArr;
  }

  get relatedObjectModelListViewIdsObj() {
    return this.related_object_model_list_view_ids.reduce((obj, pair) => {
      const key = Object.keys(pair)[0]
      const val = pair[key]
      obj[key] = val
      return obj
    }, {})
  }

  get friendlyRelationDisplay(): string {
    return friendlyObjectRelationDisplay(this.relation_type);
  }

  get relatesToSingle(): boolean {
    switch (this.relationType) {
      case ObjectModelRelationType.OneToOne:
      case ObjectModelRelationType.ManyToOne: // intentional fall-through
        return true
      case ObjectModelRelationType.OneToMany:
        return false
      case ObjectModelRelationType.Account:
        return true;
      case ObjectModelRelationType.EntityAccount:
        return true;
      default:
        assertNever(this.relationType)
    }
  }

  get relatesToMany(): boolean {
    return !this.relatesToSingle
  }

  get hasMatchingEmails(): boolean {
    const emailsPresent = !!this.new_email && !!this.new_email_confirmation;
    const emailsMatch = this.new_email.toLowerCase() === this.new_email_confirmation.toLowerCase();
    return emailsPresent && emailsMatch;
  }

  // ******************** Relative Perspective Helpers ******************** //
  // In cases where we are loading in ObjectModels from both sides of a relationship,
  // `this.rootModel` is not reliable for determining a given OM's perspective (inverse vs. non-inverse)
  // ex. OM1.object_model_relations => [<ObjectModelRelation id: 1>], OM2.inverse_object_model_relations => [<ObjectModelRelation id: 1>];
  // both OMs reference same ObjectModelRelation object, but `this.rootModel` will be whichever OM was loaded from Json first.

  friendlyDescriptionFor(inverse: boolean): string {
    const left = inverse ? this.inverseLabelTitleDisplay : this.labelTitleDisplay;
    const right = inverse ? this.labelTitleDisplay : this.inverseLabelTitleDisplay;
    return `${left} ${friendlyObjectRelationDisplay(this.relation_type, inverse).toLowerCase()} ${right}`;
  }

  relatesToManyFrom(inverse: boolean): boolean {
    const relationType = this.relationTypeFrom(inverse);
    switch (relationType) {
      case ObjectModelRelationType.OneToOne:
      case ObjectModelRelationType.ManyToOne: // intentional fall-through
        return false;
      case ObjectModelRelationType.OneToMany:
        return true;
      case ObjectModelRelationType.Account:
        return false;
      case ObjectModelRelationType.EntityAccount:
        return false;
      default:
        assertNever(relationType);
    }
  }

  relationTypeFrom(inverse: boolean): ObjectModelRelationType {
    if (inverse) {
      return ObjectModelRelationDefinition.inverseRelationshipType(this.relation_type);
    } else {
      return this.relation_type;
    }
  }

  relatedObjectModelsFor(inverse: boolean): ObjectModel[] {
    if (inverse) {
      return [this.rootObjectModel];
    }
    return this.related_object_models;
  }

  labelTitleOrDefaultFor(inverse: boolean): string {
    if (inverse) {
      return this.inverseLabelTitleOrDefault;
    }
    return this.labelTitleOrDefault;
  }
  // ******************** Relative Perspective Helpers End ******************** //

  get referenceNodeType(): ReferenceNodeType {
    return ReferenceNodeType.ObjectModelRelation
  }

  get genericType(): GenericType {
    return ModelType.Object
  }

  get modelType(): ModelType.ObjectModelRelation {
    return ModelType.ObjectModelRelation
  }

  imposterFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposter {
    return new Imposter(this.imposterMapFor({ modelType, options }), options)
  }

  imposterMapFor({ modelType, options }: { modelType: ModelType; options?: IImposterOptions }): IImposterMap {
    const mapMusts: Partial<IImposterMap> = {
      isCollection: o => o.isCollection || this.relatesToManyFrom(o.inverse),
    }
    let map: IImposterMap = ObjectHelper.merge(
      new DefaultImposterMapFactory().imposterMapFrom(this),
      mapMusts,
    )

    switch (modelType) {
      case ModelType.User:
        map.genericType = ModelType.Object
        map.id = null

        if (options.instructor instanceof ObjectModelRelationPathService && options.instructor.isAccount) {
          const targetRelation = options.instructor.reference()
          if (targetRelation.user_type !== this.user_type) {
            // not compatible if of different user_type
            map.genericType = null
            map.id = null
          }
        }
        break
      case ModelType.Email: // fallthrough
      case ModelType.Text:
      case ModelType.ObjectModelProperty:
      case ModelType.FormElement:
        map.genericType = ModelType.Text
        map.id = null
        break
      case ModelType.ObjectModel:
        /**
         * Always let relations "impose" object models. The object models themselves will do the real comparisons
         * Done this way so deeply nested relations work easily, without having to calculate all the levels at every level
         */
        map.id = o => o.target.id
        break
    }


    if (this.relationType === ObjectModelRelationType.Account) {
      map.modelType = ModelType.User;
    }

    return map
  }

  get referenceNameOrDefault(): string {
    return this.label_title;
  }

  get isValidForConditionalLogic(): boolean {
    return true;
  }

  targetTypes(): TargetType[] {
    return [];
  }

  displayConditionType(targetType: TargetType, parent = null ): ComparisonType {
    if (this.isAccountRelationship && parent instanceof ListView) {
      return ComparisonType.accountRelationUser;
    }else{
      return ComparisonType.accountRelation;
    }
  }

  getChoiceComparisonOptions(): any[] {
    return [];
  }

  getConditionDateTimeMode(): DatetimeMode {
    return null;
  }

  getConditionMinDateTime(): string {
    return "";
  }

  getConditionMaxDateTime(): string {
    return "";
  }

  // ******************** VALIDATABLE******************** //
  _validation: ObjectModelRelationValidation = new ObjectModelRelationValidation(this);

  getDisplayName(propName: string, value?): string {
    switch (propName) {
      default:
        return propName;
    }
  }

  get isPortalUserType(): boolean {
    return this.user_type === ModelType.PortalUser
  }

  get isFormabilityUserType(): boolean {
    return this.user_type === ModelType.FormabilityUser
  }

  initializeEmbedded(parent: IObjectModelRelation & ITrackedModel, item: ObjectModelRelationUserTypeFilterPermittee , position: number = null, json: object = {}, selectItem: boolean = true) {
    item.triggerChanges = true
    item.parent = parent
    item.startTracking()
    this.placeInDictionary(item, item.rootName, this)
    parent.trackCreationDeletion(item.client_id, null, item.asJson(), item);
    return item;
  }

  get canBeRequired(): boolean {
    return this.can(ObjectModelRelationCapability.requireable);
  }
}